import React, { useEffect, useState, useContext, forwardRef } from 'react'
import { useHistory } from 'react-router-dom'
import { useViewportSize } from '@mantine/hooks'
import { motion, AnimatePresence } from 'framer-motion'
import DualLangHeader from '../components/DualLangHeader'
import Plan from '../components/Plan'
import RedButton from '../components/RedButton'
import RegistrationForm from '../components/RegistrationForm'
import SubscriptionFlow from 'src/components/pages/account/subscriptions/SubscriptionFlow'
import { SubscriptionContext } from 'src/contexts/SubscriptionContext'
import { GeneralHeaderText, ColoredSpan, kfzCharLogin, tomokoLogin, logoFromZeroFooter } from '../styles'
import { CardDeck, CourseCard } from 'src/components/fz_courses/admin/CoursesPreviewPage'
import {
  ChoosePlanSectionWrapper,
  DualLangEnglishSplit,
  PlansContainer,
  PaymentContainer,
  Wave,
  Footer,
  FZLogo,
  TomokoLogin,
  Trademark,
  FooterLinks,
  FooterLink
} from '../styles/ChoosePlanSection.styles'

import useIsLoggedIn from 'src/hooks/useIsLoggedIn'
import useMobile from 'src/hooks/useMobile'

// have to make this dumb wrapper componet for DualLangHeader cause my design is dumb
const HeaderText = ({ stage, language = 'Japanese' }) => {
  const { width } = useViewportSize()
  const breakpoints = [452, 623, 422]
  const split = width < breakpoints[stage]
  const style = split ? { justifySelf: 'center', lineHeight: split ? 0.75 : '' } : {}

  const headerText = [
    {
      en: (
        <DualLangEnglishSplit breakpoint={breakpoints[0]}>
          <GeneralHeaderText>Choose <ColoredSpan color='blue'>your</ColoredSpan></GeneralHeaderText>
          <GeneralHeaderText style={style}>plan!</GeneralHeaderText>
        </DualLangEnglishSplit>
      ),
      jp: 'プランの選択',
      kr: '요금제 선택'
    },
    {
      en: (
        <DualLangEnglishSplit breakpoint={breakpoints[1]}>
          <GeneralHeaderText>Create <ColoredSpan color='blue'>your</ColoredSpan></GeneralHeaderText>
          <GeneralHeaderText style={style}>Account!</GeneralHeaderText>
        </DualLangEnglishSplit>
      ),
      jp: 'アカウントの作成',
      kr: '계정 만들기'
    },
    {
      en: (
        <DualLangEnglishSplit breakpoint={breakpoints[2]}>
          <GeneralHeaderText>Set Up</GeneralHeaderText>
          <GeneralHeaderText style={style}><ColoredSpan color='blue'>Payment!</ColoredSpan></GeneralHeaderText>
        </DualLangEnglishSplit>
      ),
      jp: '支払方法の設定',
      kr: '결제 설정'
    },
    {
      en: (
        <DualLangEnglishSplit breakpoint={breakpoints[3]}>
          <GeneralHeaderText>Start&nbsp;</GeneralHeaderText>
          <GeneralHeaderText style={style}>learning!</GeneralHeaderText>
        </DualLangEnglishSplit>
      ),
      jp: '学習を開始',
      kr: '학습 시작'
    }
  ]

  return (
    <DualLangHeader otherLang={headerText[stage][language === 'Japanese' ? 'jp' : 'kr']} blue>
      {headerText[stage].en}
    </DualLangHeader>
  )
}

const ChoosePlanSection = (props, ref) => {
  const [stage, setStage] = useState(0)
  const [selectedPlan, setSelectedPlan] = useState<'free' | 'monthly' | 'yearly'>('monthly')
  const [registerSuccess, setRegisterSuccess] = useState(false)
  const { onPlanChosen } = useContext(SubscriptionContext)
  const history = useHistory()
  const [isLoggedIn] = useIsLoggedIn()
  
  const shouldGoHome = registerSuccess && selectedPlan === 'free'
  const date = new Date()
  const year = date.getFullYear()

  // assumes initial assignment of 'monthly' to selectedPlan from context
  const handlePlanSelection = (plan: 'free' | 'monthly' | 'yearly') => {
    setSelectedPlan(plan)
    if (plan === 'free') return

    onPlanChosen(plan === 'monthly' ? 'premium-monthly' : 'premium-annual')
  }

  useEffect(() => {
    handlePlanSelection('monthly')
  }, [])
  
  const goHome = () => history.replace({ pathname: '/home' })
  
  const handleNext = () => {
    if (registerSuccess) {
      if (shouldGoHome) goHome()
      else setStage(2)
    } else setStage(1)
  }
  
  const handleRegisterSuccess = () => {
    setRegisterSuccess(true)
    if (selectedPlan === 'free') goHome()
    else setStage(2)
  }
  
  // animation properties
  // const right = { x: 1000, opacity: 0 }
  // const left = { x: -1000, opacity: 0 }
  // const center = { x: 0, opacity: 1 }
  const transition = { type: 'spring', stiffness: 400, damping: 80 }
  // const showAnimations = width > 1025
  // const defaultAnimations = { initial: right, animate: center, exit: left, transition }
  // const noAnimation = { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 }, transition: { duration: 0 } }
  // const animations = showAnimations ? defaultAnimations : noAnimation
  
  return (
    <ChoosePlanSectionWrapper ref={ref} language={props.language}>
      {!isLoggedIn && <HeaderText stage={stage} language={props.language} />}
      {isLoggedIn && props.language === 'Korean' && (
        <>
          <HeaderText stage={3} language={props.language} />
          <CardDeck style={{ justifyContent: 'center', marginTop: '30px' }}>
            {['Korean From Zero! 1', 'Korean From Zero! 2', 'Korean From Zero! 3'].map((c, index) => (
              <CourseCard
                key={c}
                title={c}
                link={`/courses/korean${index + 1}`}
                beta
              />
            ))}
          </CardDeck>
        </>
      )}
      {stage === 0 && !isLoggedIn && (
        <PlansContainer
          key={'stage-1'}
          // {...animations}
        >
          <Plan type='free' language={props.language} selectedPlan={selectedPlan} onClick={() => handlePlanSelection('free')} />
          <Plan type='monthly' language={props.language} selectedPlan={selectedPlan} onClick={() => handlePlanSelection('monthly')} />
          <Plan type='yearly' language={props.language} selectedPlan={selectedPlan} onClick={() => handlePlanSelection('yearly')} registerSuccess={registerSuccess} />
          <RedButton language={props.language} onClick={handleNext} style={{ gridColumn: '1 / -1', justifySelf: 'center' }}>
            {shouldGoHome ? 'Homeroom' : 'Next'}
          </RedButton>
        </PlansContainer>
      )}
      {stage === 1 && !isLoggedIn && (
        <motion.div
          key={'stage-2'}
          // {...animations}
        >
          <RegistrationForm onSuccess={handleRegisterSuccess} language={props.language?.toLowerCase() || 'japanese'} />
        </motion.div>
      )}
      {stage === 2 && !isLoggedIn && (
        <PaymentContainer
          key={'stage-3'}
          // {...animations}
        >
          <Plan type={selectedPlan} selectedPlan={selectedPlan} onClick={() => setStage(0)} paymentStage registerSuccess={registerSuccess} />
          <SubscriptionFlow location='landing' />
        </PaymentContainer>
      )}
      <motion.div key={'footer'} layout transition={transition} style={{ justifySelf: 'stretch' }}>
        <Wave />
        <Footer>
          <Trademark>© {year} FromZero LLC</Trademark>
          <FZLogo src={logoFromZeroFooter} alt='FromZero!' />
          <TomokoLogin src={props.language === 'Korean' ? kfzCharLogin : tomokoLogin} alt='Tomoko pointing' />
          <FooterLinks>
            <FooterLink href='https://www.fromzero.com/privacy' target='_blank' rel='noopener noreferrer'>Privacy Policy</FooterLink> |{' '}
            <FooterLink href='https://www.fromzero.com/tos' target='_blank' rel='noopener noreferrer'>ToS</FooterLink> |{' '}
            <FooterLink href='https://www.fromzero.com/ask' target='_blank' rel='noopener noreferrer'>AaT</FooterLink> |{' '}
            <FooterLink href='https://www.fromzero.com/support' target='_blank' rel='noopener noreferrer'>Support</FooterLink>
          </FooterLinks>
        </Footer>
      </motion.div>
    </ChoosePlanSectionWrapper>
  )
}

export default forwardRef(ChoosePlanSection)
